import { Placement } from "@popperjs/core";
import React, { MouseEvent, useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { usePopper } from "react-popper";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import styled from "styled-components";

import { useOutsideClick } from "../../hooks/useOutsideClick";

const Styled = styled.div`
  background-color: rgba(0, 0, 0, 0.8);
  opacity: 0;
  color: #fff;
  text-align: center;
  border-radius: 0.3rem;
  padding: 0.5rem;
  z-index: 9;

  transition: opacity 200ms;
  opacity: 0;

  &.menu-enter-active {
    opacity: 1;
  }
  &.menu-enter-done {
    opacity: 1;
  }
  &.menu-exit-active {
    opacity: 0;
  }
  &.menu-exit-done {
    opacity: 0;
  }
`;

export type TooltipPlacement = Placement;
export type TooltipOffset = [number, number];

type TooltipChildProps = {
  ref: React.Ref<any>;
} & React.DOMAttributes<HTMLElement>;

interface Props {
  disabled?: boolean;
  tip: React.ReactNode;
  placement?: TooltipPlacement;
  offset?: [number, number];
  /** useful for debugging a tooltip */
  disableHide?: boolean;
  portalTarget?: Element | null;
  showOn?: "hover" | "click";
}

const Tooltip = (props: React.PropsWithChildren<Props>) => {
  const [show, setShow] = useState(false);
  const [refElement, setRefElement] = useState<HTMLElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);
  const { styles, attributes, update } = usePopper(refElement, popperElement, {
    modifiers: [
      {
        name: "offset",
        options: {
          offset: props.offset || [0, 7],
        },
      },
    ],
    placement: props.placement || "bottom",
  });

  useOutsideClick(refElement, () => {
    if (props.showOn !== "click") return;
    setShow(false);
  });

  useEffect(() => {
    update?.();
  }, [props.tip, update]);

  const child = useMemo(() => {
    const children = React.Children.toArray(props.children).filter((c) => {
      return React.isValidElement(c);
    });

    if (children.length > 1) {
      console.error(
        "Tooltip cannot render a compoent with multiple root elements.",
      );
      return props.children;
    }

    if (
      !!children.length &&
      React.isValidElement<TooltipChildProps>(children[0])
    ) {
      return React.cloneElement(children[0], {
        ref: setRefElement,
        ...(props.showOn === "click"
          ? {
              onClick: (e: MouseEvent) => {
                setShow((value) => !value);
                e.preventDefault();
              },
              onBlur: () => {
                setShow(false);
              },
            }
          : {
              onMouseEnter: () => {
                setShow(true);
              },
              onMouseLeave: () => {
                if (props.disableHide) return;
                setShow(false);
              },
            }),
      });
    }
  }, [props.children, props.disableHide, props.showOn]);

  if (props.disabled) {
    return <>{props.children}</>;
  }

  /**
   * Note: if position of tooltip is not getting set, the ref is not attaching
   * successfully to the cloned element. You might be trying to use this this
   * outside a component that doesn't forward its ref.
   */

  return (
    <>
      <TransitionGroup component={null}>
        {show && (
          <CSSTransition classNames="menu" timeout={0} in={show}>
            {props.portalTarget != null ? (
              <>
                {ReactDOM.createPortal(
                  <Styled
                    ref={setPopperElement}
                    style={styles.popper}
                    {...attributes.popper}
                  >
                    {props.tip}
                  </Styled>,
                  props.portalTarget,
                )}
              </>
            ) : (
              <Styled
                ref={setPopperElement}
                style={styles.popper}
                {...attributes.popper}
              >
                {props.tip}
              </Styled>
            )}
          </CSSTransition>
        )}
      </TransitionGroup>
      {child}
    </>
  );
};

export default Tooltip;
